import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Card,
  CardBody,
  Avatar,
  Divider,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure
} from '@heroui/react';
import {
  Send,
  Image as ImageIcon,
  MapPin,
  BarChart3,
  Calendar,
  X,
  Plus,
  Trash2,
  Search
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ImageUploader from './ImageUploader';
import LocationPicker from './LocationPicker';

const PostEditor = ({ onPostCreated, editMode = false, initialPost = null }) => {
  const { user } = useAuth();
  const [title, setTitle] = useState(editMode && initialPost ? initialPost.title : '');
  const [text, setText] = useState(editMode && initialPost ? initialPost.text || '' : '');
  const [category, setCategory] = useState(editMode && initialPost ? initialPost.post_category?.name || 'Geral' : 'Geral');
  const [isExpanded, setIsExpanded] = useState(editMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [objectivityPassed, setObjectivityPassed] = useState(false);
  const [passedScore, setPassedScore] = useState(null);
  const [analysisRequired, setAnalysisRequired] = useState(true);
  const [lastAnalyzedContent, setLastAnalyzedContent] = useState({ title: '', text: '' });

  // Poll options state
  const [pollOptions, setPollOptions] = useState(
    editMode && initialPost?.poll_options
      ? initialPost.poll_options.map(option => option.label || option)
      : ['', '']
  );

  // Event state
  const [eventDate, setEventDate] = useState(
    editMode && initialPost?.event_date
      ? new Date(initialPost.event_date).toISOString().split('T')[0]
      : ''
  );
  const [eventTime, setEventTime] = useState(
    editMode && initialPost?.event_date
      ? new Date(initialPost.event_date).toTimeString().slice(0, 5)
      : ''
  );

  // Reset analysis requirement when content changes
  useEffect(() => {
    if (title !== lastAnalyzedContent.title || text !== lastAnalyzedContent.text) {
      if (lastAnalyzedContent.title || lastAnalyzedContent.text) { // Only reset if we had previous content
        setAnalysisRequired(true);
        setObjectivityPassed(false);
        setPassedScore(null);
      }
    }
  }, [title, text, lastAnalyzedContent]);

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    editMode && initialPost?.image_url && initialPost.image_url !== '/images/original/missing.png'
      ? (initialPost.image_url.startsWith('//') ? `https:${initialPost.image_url}` : initialPost.image_url)
      : null
  );

  // Location state for Security posts
  const [selectedLocation, setSelectedLocation] = useState(
    editMode && initialPost?.location
      ? { lat: initialPost.location[1], lng: initialPost.location[0] }
      : null
  );
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  const { isOpen: isAnalysisOpen, onOpen: onAnalysisOpen, onOpenChange: onAnalysisOpenChange } = useDisclosure();
  const { isOpen: isImageUploaderOpen, onOpen: onImageUploaderOpen, onOpenChange: onImageUploaderOpenChange } = useDisclosure();
  const { isOpen: isLocationPickerOpen, onOpen: onLocationPickerOpen, onOpenChange: onLocationPickerOpenChange } = useDisclosure();

  const categories = [
    { key: 'Geral', label: 'Geral' },
    { key: 'Segurança', label: 'Segurança' },
    { key: 'Eventos', label: 'Eventos' },
    { key: 'Votação', label: 'Votação' }, // Database key vs Display label
    { key: 'Marketplace', label: 'Marketplace' }
  ];

  // Poll helper functions
  const addPollOption = () => {
    if (pollOptions.length < 5) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index) => {
    if (pollOptions.length > 2) {
      const newOptions = pollOptions.filter((_, i) => i !== index);
      setPollOptions(newOptions);
    }
  };

  const updatePollOption = (index, value) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  // Reset form when category changes
  useEffect(() => {
    if (category === 'Votação') {
      // Reset poll options when switching to poll
      setPollOptions(['', '']);
    } else if (category === 'Eventos') {
      // Reset event fields when switching to event
      setEventDate('');
      setEventTime('');
    }
  }, [category]);

  // Validation functions
  const isValidEventDateTime = () => {
    if (category !== 'Eventos') return true;

    if (!eventDate || !eventTime) {
      return false;
    }

    // Check if the event date/time is in the future
    const eventDateTime = new Date(`${eventDate}T${eventTime}`);
    const now = new Date();

    return eventDateTime > now;
  };
  const handleImageSave = (imageData) => {
    setSelectedImage(imageData);
    setImagePreview(imageData.dataURL);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const dataURIToBlob = (dataURI) => {
    const splitDataURI = dataURI.split(',');
    const byteString = splitDataURI[0].indexOf('base64') >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
    const mimeString = splitDataURI[0].split(':')[1].split(';')[0];

    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('PostEditor: handleSubmit called with category:', category);

    if (!title.trim()) {
      alert('Por favor, adicione um título para sua postagem.');
      return;
    }

    // Validate poll options if it's a poll
    if (category === 'Votação') {
      const validOptions = pollOptions.filter(option => option.trim());
      if (validOptions.length < 2) {
        alert('Por favor, adicione pelo menos 2 opções para a votação.');
        return;
      }
    }

    // Validate event fields if it's an event
    if (category === 'Eventos') {
      if (!eventDate || !eventTime) {
        alert('Por favor, defina a data e hora do evento.');
        return;
      }
    }

    console.log('PostEditor: Starting submission process for category:', category);
    setIsSubmitting(true);

    try {
      // Perform automatic objectivity analysis for regular posts
      // Exclude categories that need immediate posting or are inherently subjective
      let objectivityScore = null;

      console.log('PostEditor: Checking if objectivity analysis is needed...');
      console.log('PostEditor: category value:', JSON.stringify(category));
      console.log('PostEditor: category === "Segurança":', category === 'Segurança');
      console.log('PostEditor: category !== "Votação":', category !== 'Votação');
      console.log('PostEditor: category !== "Eventos":', category !== 'Eventos');
      console.log('PostEditor: category !== "Segurança":', category !== 'Segurança');
      console.log('PostEditor: text.trim():', !!text.trim());
      console.log('PostEditor: Full condition result:', (category !== 'Votação' && category !== 'Eventos' && category !== 'Segurança' && text.trim()));

      if (category !== 'Votação' && category !== 'Eventos' && category !== 'Segurança' && text.trim() && analysisRequired) {
        console.log('PostEditor: Performing automatic objectivity analysis...');
        const analysisResult = await performAutomaticAnalysis();

        if (!analysisResult.canPost) {
          setIsSubmitting(false);
          setAnalysisResult(analysisResult.results);
          onAnalysisOpen();
          console.log(`PostEditor: Post blocked due to low objectivity score: ${analysisResult.score}%. Minimum required: 70%.`);
          return;
        }

        if (analysisResult.results) {
          objectivityScore = analysisResult.score;
          console.log(`PostEditor: Objectivity check passed with score: ${analysisResult.score}%`);

          // Show success indicator
          setObjectivityPassed(true);
          setPassedScore(analysisResult.score);

          // Hide success indicator after 3 seconds
          setTimeout(() => {
            setObjectivityPassed(false);
            setPassedScore(null);
          }, 3000);
        }
      } else if (category === 'Segurança') {
        console.log('PostEditor: Skipping objectivity analysis for security post - immediate posting allowed');
      } else {
        console.log('PostEditor: Skipping objectivity analysis - category or content conditions not met');
      }

      console.log('PostEditor: Creating post:', { title, text, category });

      // Use FormData for better compatibility with Rails API
      const formData = new FormData();
      formData.append('post[title]', title.trim());
      formData.append('post[text]', text.trim());
      formData.append('post[post_category]', category);

      // Add objectivity score if available
      if (objectivityScore !== null) {
        formData.append('post[objectivity_score]', objectivityScore);
      }

      // Add poll options if it's a poll
      if (category === 'Votação') {
        const validOptions = pollOptions.filter(option => option.trim());

        // Add each poll option as a separate array element
        validOptions.forEach(option => {
          formData.append('post[poll_options][]', option.trim());
        });
        formData.append('post[type]', 'poll');
      }

      // Validate event data if it's an event
      if (category === 'Eventos') {
        if (!eventDate || !eventTime) {
          alert('Por favor, defina a data e hora do evento.');
          return;
        }

        if (!isValidEventDateTime()) {
          alert('A data e hora do evento devem ser no futuro.');
          return;
        }

        const eventDateTime = new Date(`${eventDate}T${eventTime}`);
        formData.append('post[event_date]', eventDateTime.toISOString());
        formData.append('post[type]', 'event');
      }

      // Add location if it's a security post with location
      if (category === 'Segurança' && selectedLocation) {
        formData.append('post[location][]', selectedLocation.lng);
        formData.append('post[location][]', selectedLocation.lat);
      }

      // Add image if one is selected
      if (selectedImage && selectedImage.dataURL) {
        const imgBlob = dataURIToBlob(selectedImage.dataURL);
        formData.append('post[image]', imgBlob, 'image.png');
      }

      const apiUrl = editMode ? `/api/v1/posts/${initialPost._id?.['$oid'] || initialPost._id}` : '/api/v1/posts';
      const method = editMode ? 'PUT' : 'POST';

      console.log(`PostEditor: About to make API call to ${apiUrl} with method ${method}`);
      console.log('PostEditor: FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      const response = await fetch(apiUrl, {
        method: method,
        credentials: 'include',
        body: formData // Use FormData instead of JSON
      });

      console.log('PostEditor: API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`PostEditor: Post ${editMode ? 'updated' : 'created'} successfully:`, data);

        if (!editMode) {
          // Clear form only for create mode
          setTitle('');
          setText('');
          setCategory('Geral');
          setPollOptions(['', '']);
          setEventDate('');
          setEventTime('');
          setSelectedImage(null);
          setImagePreview(null);
          setSelectedLocation(null);
          setIsExpanded(false);
          setAnalysisResult(null);
          setObjectivityPassed(false);
          setPassedScore(null);
          setAnalysisRequired(true);
          setLastAnalyzedContent({ title: '', text: '' });
        }

        // Notify parent
        if (onPostCreated) {
          onPostCreated(data.posts);
        }

        // Show success message
        console.log(`PostEditor: Post ${editMode ? 'updated' : 'created'} successfully`);
      } else {
        console.error('PostEditor: API response not ok:', response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error('PostEditor: Error response JSON:', errorData);
          alert(errorData.error || 'Erro ao criar postagem');
        } catch (parseError) {
          console.error('PostEditor: Could not parse error response as JSON:', parseError);
          const errorText = await response.text();
          console.error('PostEditor: Error response text:', errorText);
          alert('Erro ao criar postagem');
        }
      }
    } catch (error) {
      console.error('PostEditor: Network error creating post:', error);
      console.error('PostEditor: Error stack:', error.stack);
      alert('Erro de rede ao criar postagem');
    } finally {
      console.log('PostEditor: Submission process completed, setting isSubmitting to false');
      setIsSubmitting(false);
    }
  };

  const analyzeObjectivity = async () => {
    console.log('analyzeObjectivity called', { title: title.trim(), text: text.trim() });

    if (!title.trim()) {
      alert('Por favor, escreva um título para analisar.');
      return;
    }

    setIsAnalyzing(true);

    try {
      const formData = new FormData();
      // Analyze title and body together if body exists, otherwise just title
      const contentToAnalyze = text.trim() ? `${title.trim()} ${text.trim()}` : title.trim();
      formData.append('body', contentToAnalyze);

      const response = await fetch('/api/v1/analyzer', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('PostEditor: Analysis result:', data);
        setAnalysisResult(data.results);

        // Check if analysis passes threshold
        if (data.results && data.results.objectivity >= 60) {
          setAnalysisRequired(false);
          setObjectivityPassed(true);
          setPassedScore(data.results.objectivity);
          setLastAnalyzedContent({ title, text });
        } else {
          onAnalysisOpen();
        }

        return data.results;
      } else {
        console.error('PostEditor: Analysis failed');
        alert('Erro ao analisar objetividade');
        return null;
      }
    } catch (error) {
      console.error('PostEditor: Analysis error:', error);
      alert('Erro de rede ao analisar');
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Automatic analysis function for pre-post validation
  const performAutomaticAnalysis = async () => {
    if (!text.trim()) {
      return { canPost: true }; // Allow posts without text content
    }

    try {
      const formData = new FormData();
      formData.append('body', text);

      const response = await fetch('/api/v1/analyzer', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        console.log('PostEditor: Automatic analysis result:', data);

        const objectivityScore = data.results?.objectivity_score || 0;
        const canPost = objectivityScore >= 70;

        console.log(`PostEditor: Objectivity analysis - Score: ${objectivityScore}, Can Post: ${canPost}, Raw Score: ${data.results?.objectivity_score}`);

        return {
          canPost,
          score: objectivityScore,
          results: data.results
        };
      } else {
        console.error('PostEditor: Automatic analysis failed');
        // Allow posting if analysis fails to avoid blocking users
        return { canPost: true };
      }
    } catch (error) {
      console.error('PostEditor: Automatic analysis error:', error);
      // Allow posting if analysis fails to avoid blocking users
      return { canPost: true };
    }
  };

  const getAnalysisColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const getAnalysisText = (score) => {
    if (score >= 80) return `Excelente objetividade: ${score}%`;
    if (score >= 60) return `Objetividade moderada: ${score}%`;
    if (score >= 40) return `Baixa objetividade: ${score}%`;
    return `Objetividade muito baixa: ${score}%`;
  };

  const getAnalysisIcon = (score) => {
    if (score >= 80) return '✅';
    if (score >= 60) return '⚠️';
    if (score >= 40) return '⚠️';
    return '❌';
  };

  const handleCancel = () => {
    setTitle('');
    setText('');
    setCategory('Geral');
    setPollOptions(['', '']);
    setEventDate('');
    setEventTime('');
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedLocation(null);
    setIsExpanded(false);
    setAnalysisResult(null);
    setObjectivityPassed(false);
    setPassedScore(null);
    setAnalysisRequired(true);
    setLastAnalyzedContent({ title: '', text: '' });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <Avatar
            src={user?.avatar}
            name={user?.username}
            size="sm"
            className="flex-shrink-0"
          />

          <div className="flex-1">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title Input */}
              <Input
                placeholder={`O que está acontecendo, ${user?.username}?`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onFocus={() => setIsExpanded(true)}
                variant="bordered"
                size="lg"
                classNames={{
                  input: "text-lg",
                  inputWrapper: "border-2 border-gray-300 hover:border-primary-400 focus-within:border-primary-500 bg-white shadow-sm rounded-nextui",
                  base: "rounded-nextui"
                }}
              />

              {/* Expanded Content */}
              {isExpanded && (
                <div className="space-y-4">
                  <Textarea
                    placeholder="Compartilhe mais detalhes sobre sua postagem..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    variant="bordered"
                    minRows={3}
                    maxRows={8}
                    classNames={{
                      inputWrapper: "border-2 border-gray-300 hover:border-primary-400 focus-within:border-primary-500 bg-white shadow-sm rounded-nextui",
                      base: "rounded-nextui"
                    }}
                  />

                  <Divider />

                  {/* Category and Actions */}
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center space-x-2 flex-wrap">
                      <Select
                        size="sm"
                        variant="bordered"
                        selectedKeys={[category]}
                        onSelectionChange={(keys) => setCategory(Array.from(keys)[0])}
                        className="w-36"
                        placeholder="Categoria"
                        classNames={{
                          trigger: "border-2 border-gray-300 hover:border-primary-400 focus-within:border-primary-500 bg-white shadow-sm rounded-nextui-sm",
                          popoverContent: "bg-white shadow-2xl border border-gray-200 rounded-nextui",
                          listbox: "bg-white"
                        }}
                      >
                        {categories.map((cat) => (
                          <SelectItem key={cat.key} value={cat.key}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </Select>

                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        className="text-gray-500 hover:text-primary"
                        title="Adicionar imagem"
                        onPress={onImageUploaderOpen}
                      >
                        <ImageIcon className="w-4 h-4" />
                      </Button>

                      <Button
                        isIconOnly
                        size="sm"
                        variant="light"
                        className={`text-gray-500 hover:text-primary ${category === 'Segurança' ? '' : 'opacity-50 cursor-not-allowed'}`}
                        title={category === 'Segurança' ? 'Adicionar localização do incidente' : 'Localização disponível apenas para posts de Segurança'}
                        onPress={category === 'Segurança' ? onLocationPickerOpen : undefined}
                        isDisabled={category !== 'Segurança'}
                      >
                        <MapPin className="w-4 h-4" />
                      </Button>

                      {category !== 'Votação' && category !== 'Eventos' && (
                        <div></div>
                      )}
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="light"
                        onPress={handleCancel}
                        className="text-gray-500"
                      >
                        Cancelar
                      </Button>

                      {(analysisRequired && category !== 'Votação' && category !== 'Eventos') ? (
                        <Button
                          color="primary"
                          size="sm"
                          onPress={() => {
                            console.log('Analisar button clicked');
                            analyzeObjectivity();
                          }}
                          startContent={
                            isAnalyzing ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Search className="w-4 h-4" />
                            )
                          }
                          isLoading={isAnalyzing}
                          isDisabled={!title.trim() || isAnalyzing}
                          className="font-semibold bg-blue-500 hover:bg-blue-600"
                        >
                          Analisar
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          color="success"
                          size="sm"
                          startContent={
                            isSubmitting ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )
                          }
                          isLoading={isSubmitting}
                          isDisabled={!title.trim()}
                          className="font-semibold bg-green-500 hover:bg-green-600"
                        >
                          {editMode ? 'Atualizar' : 'Publicar'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Objectivity Check Success Indicator */}
                  {objectivityPassed && (
                    <div className="flex items-center justify-center p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center text-green-700">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                        <span className="text-sm font-medium">
                          Verificação de objetividade aprovada ({passedScore}%)
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Poll Options */}
                  {category === 'Votação' && (
                    <Card className="bg-blue-50 border-blue-200 rounded-nextui">
                      <CardBody className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-blue-800 flex items-center">
                              <BarChart3 className="w-4 h-4 mr-2" />
                              Opções da Votação
                            </h4>
                            {pollOptions.length < 5 && (
                              <Button
                                size="sm"
                                variant="light"
                                startContent={<Plus className="w-4 h-4" />}
                                onPress={addPollOption}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Adicionar
                              </Button>
                            )}
                          </div>

                          <div className="space-y-2">
                            {pollOptions.map((option, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <span className="text-sm text-blue-600 font-medium min-w-[60px]">
                                  Opção {index + 1}:
                                </span>
                                <Input
                                  size="sm"
                                  variant="bordered"
                                  placeholder={`Digite a opção ${index + 1}`}
                                  value={option}
                                  onChange={(e) => updatePollOption(index, e.target.value)}
                                  className="flex-1"
                                  classNames={{
                                    inputWrapper: "border-2 border-gray-300 hover:border-primary-400 focus-within:border-primary-500 bg-white shadow-sm rounded-nextui-sm",
                                    base: "rounded-nextui-sm"
                                  }}
                                />
                                {pollOptions.length > 2 && (
                                  <Button
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    onPress={() => removePollOption(index)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>

                          <div className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
                            💡 Adicione de 2 a 5 opções para sua votação. Os usuários poderão votar em uma das opções.
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Event Details */}
                  {category === 'Eventos' && (
                    <Card className="bg-green-50 border-green-200 rounded-nextui">
                      <CardBody className="p-4">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-green-800 flex items-center">
                            <Calendar className="w-4 h-4 mr-2" />
                            Detalhes do Evento
                          </h4>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-1">
                                Data do Evento
                              </label>
                              <input
                                type="date"
                                value={eventDate}
                                onChange={(e) => setEventDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className={`w-full px-3 py-2 text-sm border-2 rounded-nextui focus:outline-none focus:ring-2 shadow-sm bg-white input-nextui ${
                                  eventDate
                                    ? 'border-green-400 focus:ring-green-500 focus:border-green-500'
                                    : 'border-gray-300 focus:ring-green-500 focus:border-green-500 hover:border-green-400'
                                }`}
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-green-700 mb-1">
                                Horário
                              </label>
                              <input
                                type="time"
                                value={eventTime}
                                onChange={(e) => setEventTime(e.target.value)}
                                className={`w-full px-3 py-2 text-sm border-2 rounded-nextui focus:outline-none focus:ring-2 shadow-sm bg-white input-nextui ${
                                  eventTime
                                    ? 'border-green-400 focus:ring-green-500 focus:border-green-500'
                                    : 'border-gray-300 focus:ring-green-500 focus:border-green-500 hover:border-green-400'
                                }`}
                              />
                              {eventTime && (
                                <div className="text-xs text-green-600 mt-1">
                                  ✓ Horário válido: {eventTime}
                                </div>
                              )}
                              {eventDate && eventTime && (
                                <div className="text-xs text-blue-600 mt-1">
                                  📅 Evento: {new Date(`${eventDate}T${eventTime}`).toLocaleString('pt-BR')}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="text-xs text-green-600 bg-green-100 p-2 rounded">
                            📅 Defina quando seu evento acontecerá. Os usuários poderão ver os detalhes e se interessar.
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Image Preview */}
                  {imagePreview && (
                    <Card className="bg-gray-50 border-gray-200 rounded-nextui">
                      <CardBody className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-800 flex items-center">
                              <ImageIcon className="w-4 h-4 mr-2" />
                              Imagem Anexada
                            </h4>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={handleRemoveImage}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="flex justify-center">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="max-w-full max-h-48 rounded-lg border object-contain"
                            />
                          </div>

                          <div className="text-xs text-gray-600 bg-gray-100 p-2 rounded text-center">
                            📷 Imagem será anexada à sua postagem
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {/* Location Preview */}
                  {selectedLocation && (
                    <Card className="bg-blue-50 border-blue-200 rounded-nextui">
                      <CardBody className="p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-blue-800 flex items-center">
                              <MapPin className="w-4 h-4 mr-2" />
                              Localização do Incidente
                            </h4>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => setSelectedLocation(null)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="text-sm text-blue-700 bg-blue-100 p-3 rounded">
                            <div className="flex items-center justify-between">
                              <span>📍 Coordenadas selecionadas:</span>
                              <Button
                                size="sm"
                                variant="light"
                                onPress={onLocationPickerOpen}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                Alterar
                              </Button>
                            </div>
                            <div className="mt-1 font-mono text-xs">
                              Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Analysis Results Modal */}
      <Modal
        isOpen={isAnalysisOpen}
        onOpenChange={onAnalysisOpenChange}
        size="lg"
        backdrop="opaque"
        classNames={{
          backdrop: "bg-black/50",
          base: "border-none",
          wrapper: "p-4",
          body: "py-6",
          header: "border-b-[1px] border-gray-200",
          footer: "border-t-[1px] border-gray-200",
          closeButton: "hover:bg-gray-100"
        }}
      >
        <ModalContent className="bg-white shadow-2xl rounded-nextui-lg">
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1 bg-white">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <span className="text-lg font-semibold text-gray-900">Análise de Objetividade</span>
                </div>
              </ModalHeader>

              <ModalBody className="bg-white">
                {analysisResult && (
                  <div className="space-y-4">
                    {/* Score */}
                    <Card className="bg-gray-50 border-2 border-gray-200 shadow-sm rounded-nextui">
                      <CardBody className="p-4">
                        <div className="flex items-center justify-center space-x-3">
                          <span className="text-2xl">
                            {getAnalysisIcon(analysisResult.objectivity)}
                          </span>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-800">
                              {analysisResult.objectivity}%
                            </div>
                            <Chip
                              color={getAnalysisColor(analysisResult.objectivity)}
                              variant="flat"
                              size="sm"
                            >
                              {getAnalysisText(analysisResult.objectivity)}
                            </Chip>
                          </div>
                        </div>
                      </CardBody>
                    </Card>

                    {/* Suggestions */}
                    {analysisResult.suggestions && analysisResult.suggestions.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                          <span className="mr-2">💡</span>
                          Sugestões para melhorar:
                        </h4>
                        <div className="space-y-2">
                          {analysisResult.suggestions.map((suggestion, index) => (
                            <div key={index} className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <span className="text-blue-500 mt-0.5">•</span>
                              <span className="text-sm text-blue-800">{suggestion}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Explanation */}
                    <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
                      <p className="text-sm text-gray-600 font-medium mb-2">Como funciona a análise:</p>
                      <p className="text-sm text-gray-600">
                        Nossa IA analisa o texto em busca de linguagem objetiva, fatos verificáveis
                        e ausência de viés emocional para promover discussões mais construtivas na comunidade.
                      </p>
                    </div>
                  </div>
                )}
              </ModalBody>

              <ModalFooter className="bg-white">
                <Button
                  color="primary"
                  onPress={onClose}
                  className="bg-primary text-white border-2 border-primary hover:bg-primary-600"
                >
                  Entendi
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Image Uploader Modal */}
      <ImageUploader
        isOpen={isImageUploaderOpen}
        onClose={onImageUploaderOpenChange}
        onImageSave={handleImageSave}
      />

      {/* Location Picker Modal */}
      <LocationPicker
        isOpen={isLocationPickerOpen}
        onOpenChange={onLocationPickerOpenChange}
        onLocationSelect={setSelectedLocation}
      />
    </>
  );
};

export default PostEditor;
